<template>
  <div class="wecom-message-content">
    <template v-if="isInlinePreviewImage(message)">
      <div class="message-img-box">
        <auth-img
          mode="ant"
          :width="200"
          height="auto"
          :src="mediaUrl"
          :preview="false"
          :fallback="defaultImage"
          @click="emit('preview-image', message)"
        />
      </div>
    </template>
    <template v-else-if="message.msgType === MSG_TYPE.VIDEO && mediaUrl">
      <auth-img mode="video" :src="mediaUrl" :width="200" controls />
    </template>
    <template v-else-if="message.msgType === MSG_TYPE.VOICE">
      <div class="voice-row">
        <VoiceMessage :src="mediaUrl" :play-length="voicePlayLength" />
        <span v-if="voiceTranscript" class="audio-text-content">
          {{ voiceTranscript }}
        </span>
      </div>
    </template>
    <template v-else-if="fileInfo">
      <div
        class="file-item"
        :class="{ 'is-disabled': !fileInfo.mediaReady }"
        @click="handleFileOpen"
      >
        <div class="file-name">
          <div class="file-name-text" :title="fileInfo.name">{{ fileInfo.displayName }}</div>
          <div class="file-meta">
            <span v-if="fileInfo.size" class="file-size">{{ fileInfo.size }}</span>
            <span v-if="!fileInfo.mediaReady" class="file-parse-failed">解析失败</span>
          </div>
        </div>
        <img class="file-icon" :src="fileInfo.icon" alt="" />
      </div>
    </template>
    <div v-else class="text-content">{{ displayText }}</div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import {
  MSG_TYPE,
  defaultImage,
  getItemMediaUrl,
  getVoicePlayLength,
  getVoiceTranscript,
  isFileDocumentMessage,
  isInlinePreviewImage,
  openFileByExt,
  resolveFileItem
} from './utils'
import VoiceMessage from './VoiceMessage.vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})
const emit = defineEmits(['preview-image'])

const mediaUrl = computed(() => getItemMediaUrl(props.message))
const voicePlayLength = computed(() => getVoicePlayLength(props.message.content))
const voiceTranscript = computed(() => getVoiceTranscript(props.message.content))
const fileInfo = computed(() =>
  isFileDocumentMessage(props.message) ? resolveFileItem(props.message) : null
)
const displayText = computed(
  () => props.message.sendContent || props.message.msgTypeDesc || '暂不支持的消息类型'
)

const handleFileOpen = () => {
  const file = fileInfo.value
  if (!file?.mediaReady || !file.url) return
  openFileByExt({
    url: file.url,
    ext: file.ext,
    fileName: file.name
  })
}
</script>

<style scoped lang="scss">
.message-img-box {
  display: inline-block;
  overflow: hidden;
  border-radius: 4px;
  cursor: pointer;
}

.text-content,
.audio-text-content {
  color: #1b1b1b;
  font-size: 15px;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-all;
}

.voice-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audio-text-content {
  display: flex;
  width: fit-content;
  max-width: 297px;
  padding: 10px 12px;
  box-sizing: border-box;
  align-items: center;
  gap: 11px;
  border-radius: 8px;
  background: #f2f2f2;
}

.file-item {
  display: flex;
  width: 229px;
  padding: 12px;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #eff2fa;
  }

  &.is-disabled {
    cursor: default;
  }
}

.file-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.file-name {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
  color: #333;
  font-size: 16px;
  word-break: break-all;
}

.file-name-text {
  display: -webkit-box;
  max-height: 44px;
  overflow: hidden;
  line-height: 22px;
  word-break: break-all;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.file-size {
  color: #999;
  font-size: 13px;
}

.file-parse-failed {
  color: #ff4d4f;
  font-size: 13px;
}
</style>
